<template>
  <div class="feedback-section">
    <b-card style="max-width: 34rem;">
      <b-card-title class="dark-variant">Feedback Form</b-card-title>

      <!-- SENT -->
      <div v-if="sent" class="fb-done">
        <span class="material-icons fb-done-icon">mark_email_read</span>
        <h5>Thank you, {{ form.first_name }}!</h5>
<p>{{ success_message }}</p>
        <b-button variant="light" @click="resetForm">Send another response</b-button>
      </div>

      <!-- FORM -->
      <template v-else>
        <b-card-text>
          Tell us how Amejpay is working for you. Every answer goes straight to the
          product team and helps us decide what to fix and build next.
        </b-card-text>

        <b-form style="font-size:14px;" class="mt-4" @submit.prevent="submitForm">
          <label for="fb-first-name" class="dark-variant">First Name *</label>
          <b-input
            type="text"
            id="fb-first-name"
            v-model.trim="form.first_name"
            :class="errors.first_name ? 'is-invalid' : ''"
            placeholder="Your first name"
          ></b-input>
          <span class="fb-error" v-if="errors.first_name">{{ errors.first_name }}</span>

          <label for="fb-last-name" class="dark-variant">Last Name *</label>
          <b-input
            type="text"
            id="fb-last-name"
            v-model.trim="form.last_name"
            :class="errors.last_name ? 'is-invalid' : ''"
            placeholder="Your last name"
          ></b-input>
          <span class="fb-error" v-if="errors.last_name">{{ errors.last_name }}</span>

          <label for="fb-email" class="dark-variant">Email *</label>
          <b-input
            type="text"
            id="fb-email"
            v-model.trim="form.email"
            :class="errors.email ? 'is-invalid' : ''"
            placeholder="Enter your email"
          ></b-input>
          <span class="fb-error" v-if="errors.email">{{ errors.email }}</span>

          <label class="dark-variant mt-4">How satisfied you were with? *</label>
          <p class="fb-hint">
            Rate the Amejpay services you have used. Pick "Not used yet" for anything
            you have not tried.
          </p>
          <span class="fb-error d-block mb-2" v-if="errors.ratings">{{ errors.ratings }}</span>

          <b-form-group
            v-for="topic in topics"
            :key="topic.key"
            :label="topic.label"
            class="fb-topic"
          >
            <p class="fb-topic-desc">{{ topic.description }}</p>
            <b-form-radio-group
              v-model="form.ratings[topic.key]"
              :name="'fb-rating-' + topic.key"
              class="fb-radios"
              stacked
            >
              <b-form-radio
                v-for="option in ratingOptions"
                :key="topic.key + '-' + option.value"
                :value="option.value"
              >{{ option.text }}</b-form-radio>
            </b-form-radio-group>
          </b-form-group>

          <label for="fb-comments" class="dark-variant mt-4">
            Feel free to add any other comments or suggestions:
          </label>
          <b-form-textarea
            id="fb-comments"
            v-model="form.comments"
            placeholder="Message..."
            rows="3"
            max-rows="6"
          ></b-form-textarea>

          <b-card-text class="light-variant my-4">
            * The information given within the Feedback Form will be used for service
            improvement only and are strictly confidential.
          </b-card-text>

          <div class="button">
            <b-button variant="light" type="submit" :disabled="isSending">
              <span v-if="isSending">Sending...</span>
              <span v-else>Send Feedback</span>
            </b-button>
          </div>
        </b-form>
      </template>
    </b-card>

    <b-toast
      id="feedback-success-toast"
      variant="success"
      solid
      append-toast
      toaster="b-toaster-top-center"
    >
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Success!!!</strong>
      </div>
      <p>{{ success_message }}</p>
    </b-toast>

    <b-toast
      id="feedback-error-toast"
      variant="danger"
      solid
      append-toast
      toaster="b-toaster-top-center"
    >
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error!!!</strong>
      </div>
      <p>{{ error_message }}</p>
    </b-toast>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { cleanName } from "../../utils/format";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** The Amejpay services a customer can actually rate. */
const TOPICS = [
  {
    key: "load_money",
    label: "Loading money into your wallet",
    description: "Card top-ups and payment gateways under Load Money."
  },
  {
    key: "send_request",
    label: "Sending & requesting payments",
    description: "Paying another Amejpay wallet and payment requests."
  },
  {
    key: "withdraw",
    label: "Withdrawing to your bank",
    description: "Linking bank accounts and moving your balance out."
  },
  {
    key: "verification",
    label: "Account verification & security",
    description: "KYC submission, document uploads, MPIN and OTP."
  },
  {
    key: "support",
    label: "Customer support",
    description: "Response time and how helpful our answers were."
  },
  {
    key: "overall",
    label: "Your overall Amejpay experience",
    description: "The app as a whole - speed, clarity and reliability."
  }
];

const RATING_OPTIONS = [
  { value: "very_satisfied", text: "Very Satisfied" },
  { value: "satisfied", text: "Satisfied" },
  { value: "unsatisfied", text: "Unsatisfied" },
  { value: "very_unsatisfied", text: "Very Unsatisfied" },
  { value: "not_used", text: "Not used yet" }
];

function emptyRatings() {
  const out = {};
  TOPICS.forEach(function (t) { out[t.key] = null; });
  return out;
}

export default {
  name: "FeedBackSection",
  data() {
    return {
      topics: TOPICS,
      ratingOptions: RATING_OPTIONS,
      form: {
        first_name: "",
        last_name: "",
        email: "",
        ratings: emptyRatings(),
        comments: ""
      },
      errors: {},
      isSending: false,
      sent: false,
      success_message: "",
      error_message: ""
    };
  },
  computed: {
    ...mapState(["profile"]),
  },
  created() {
    this.prefillFromProfile();
  },
  watch: {
    profile() {
      if (!this.form.email) { this.prefillFromProfile(); }
    }
  },
  methods: {
    ...mapActions(["submitFeedback"]),

    /** Pre-fills name and email from the logged in profile when available. */
    prefillFromProfile() {
      const author = (this.profile && this.profile.author) || {};
      if (!this.form.first_name) {
        this.form.first_name = cleanName(author.first_name || "");
      }
      if (!this.form.last_name) {
        this.form.last_name = cleanName(author.last_name || "");
      }
      if (!this.form.email) {
        this.form.email = author.email || "";
      }
    },

    labelForRating(value) {
      const found = RATING_OPTIONS.find(function (o) { return o.value === value; });
      return found ? found.text : "No answer";
    },

    validate() {
      const e = {};
      if (!this.form.first_name) { e.first_name = "First name is required."; }
      if (!this.form.last_name) { e.last_name = "Last name is required."; }
      if (!this.form.email) {
        e.email = "Email is required.";
      } else if (!EMAIL_RE.test(this.form.email)) {
        e.email = "Please enter a valid email address.";
      }
      const vm = this;
      const anyAnswer = TOPICS.some(function (t) {
        return Boolean(vm.form.ratings[t.key]);
      });
      if (!anyAnswer) {
        e.ratings = "Please rate at least one service.";
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    /** Ratings in the shape the mail service expects. */
    buildRatings() {
      const vm = this;
      return TOPICS.map(function (t) {
        return {
          key: t.key,
          label: t.label,
          value: vm.labelForRating(vm.form.ratings[t.key])
        };
      });
    },

    /** Provider-agnostic payload - the mail service renders it. */
    buildPayload() {
      const fullName = (this.form.first_name + " " + this.form.last_name).trim();
      return {
        fullName: fullName,
        firstName: this.form.first_name,
        lastName: this.form.last_name,
        email: this.form.email,
        ratings: this.buildRatings(),
        comments: this.form.comments || "(no additional comments)",
        submittedAt: new Date().toLocaleString()
      };
    },

    submitForm() {
      if (this.isSending) { return; }
      if (!this.validate()) {
        this.error_message = "Please correct the highlighted fields.";
        this.$bvToast.show("feedback-error-toast");
        return;
      }
      const vm = this;
      this.isSending = true;

      // The backend records the feedback and sends both emails; all we do is report
      // what it tells us.
      return this.submitFeedback(this.buildPayload())
        .then(function (res) {
          vm.isSending = false;
          if (res && res.ok) {
            vm.sent = true;
            vm.success_message = res.message;
            vm.$bvToast.hide("feedback-error-toast");
            vm.$bvToast.show("feedback-success-toast");
            return;
          }
          // Field-level complaints belong under their field, not only in a toast.
          const fe = (res && res.fieldErrors) || {};
          Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
          vm.error_message = (res && res.message) ||
            "Sorry, your feedback could not be sent. Please try again.";
          vm.$bvToast.show("feedback-error-toast");
        })
        .catch(function () {
          vm.isSending = false;
          vm.error_message = "Sorry, your feedback could not be sent. Please try again.";
          vm.$bvToast.show("feedback-error-toast");
        });
    },

    resetForm() {
      this.form.ratings = emptyRatings();
      this.form.comments = "";
      this.errors = {};
      this.sent = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../css/style.scss";

.feedback-section {
  .fb-alert {
    font-size: 13px;
    margin-top: 14px;

    code {
      background: rgba(0, 0, 0, 0.05);
      padding: 0 3px;
      border-radius: 3px;
    }
  }

  .fb-hint {
    font-size: 13px;
    color: #8a94a6;
    margin: 4px 0 12px;
  }

  .fb-error {
    display: block;
    color: #dc3545;
    font-size: 12px;
    margin: 2px 0 6px;
  }

  .fb-topic {
    background: #f7f9fc;
    border: 1px solid #eceff4;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 12px;
  }

  .fb-topic-desc {
    font-size: 12px;
    color: #8a94a6;
    margin: -4px 0 8px;
  }

  .fb-radios {
    font-size: 13px;
  }

  .fb-done {
    text-align: center;
    padding: 26px 10px 14px;

    .fb-done-icon {
      font-size: 46px;
      color: #28a745;
      margin-bottom: 10px;
    }

    h5 {
      margin-bottom: 8px;
    }

    p {
      font-size: 14px;
      color: #6b7280;
      margin-bottom: 18px;
    }
  }
}

@media (min-width: 576px) {
  .feedback-section .fb-radios {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
}
</style>
